class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
  }

  get els() {
    return this.querySelectorAll('[data-announcement]');
  }

  get countdownEls() {
    return this.querySelectorAll('[data-countdown]');
  }

  get linkEls() {
    return this.querySelectorAll('[data-announcement] a');
  }

  get navNext() {
    return this.querySelector('[data-nav="next"]');
  }

  get navPrev() {
    return this.querySelector('[data-nav="prev"]');
  }

  get freeShippingMessages() {
    return this.querySelectorAll('[data-free-shipping-message]');
  }

  get freeShippingThresholds() {
    return this.querySelectorAll('[data-announcement-threshold]');
  }

  connectedCallback() {
    // Liquid fallback for no active announcements
    if (!this.els.length) {
      this.parentElement.classList.add('hide');
      return;
    }

    // Primary settings
    this.hasFreeShippingBanner = this.dataset.hasFreeShippingBanner;
    this.indexActive = 0;
    this.indexLast = this.els.length - 1;

    // Secondary settings
    this.hasAutoplay = this.dataset.autoplay && this.dataset.autoplay > 0;
    this.hasCarousel = this.els.length > 1;

    // Setup and listeners
    if (this.els.length) this.setItemAttrs();
    if (this.countdownEls.length) this.handleCountdowns();
    if (this.hasCarousel && this.hasAutoplay) this.autoplayTimerStart();
    if (this.hasCarousel) this.handleNav();
    if (this.hasFreeShippingBanner) this.freeShippingBanner();
    this.handleAutoplayOnInteraction();
  }

  // Set index-based attributes of announcement items
  setItemAttrs() {
    this.els.forEach((el, i) => {
      el.dataset.index = i;
      if (i === 0) {
        el.classList.remove('hide');
        el.classList.add('active');
      }
    });
  }

  showItem(newIndex) {
    this.fade?.finish();

    const current = this.els[this.indexActive];
    const next = this.els[newIndex];
    this.indexActive = newIndex;

    this.fade = current.animate({ opacity: [1, 0] }, { duration: 300, easing: 'ease-in-out' });
    this.fade.finished.then(() => {
      current.classList.replace('active', 'hide');
      next.classList.replace('hide', 'active');
    });
  }

  autoplayTimerStart() {
    this.timerIsPaused = false;
    this.timerIsStopped = false;

    this.timer = setInterval(() => {
      this.showItem(this.indexActive + 1 > this.indexLast ? 0 : this.indexActive + 1);
    }, this.dataset.autoplay);
  }

  autoplayTimerPause() {
    if (!this.hasAutoplay || this.timerIsPaused) return;

    this.timerIsPaused = true;
    clearInterval(this.timer);
  }

  autoplayTimerStop() {
    if (!this.hasAutoplay || this.timerIsStopped) return;

    this.timerIsStopped = true;
    clearInterval(this.timer);
  }

  freeShippingBanner() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.source === 'product-form') return;
      this.updateFreeShippingBanner(event.cartData.total_price);
    });
  }

  handleAutoplayOnInteraction() {
    if (!this.hasAutoplay || !this.hasCarousel || this.timerIsStopped) return;

    this.addEventListener('click', () => {
      this.classList.add('interacted');
    });

    this.linkEls.forEach(el => {
      el.addEventListener('click', () => {
        this.autoplayTimerPause();
      });
    });
  }

  handleNav() {
    [this.navNext, this.navPrev].forEach(el => {
      if (this.dataset.hideArrows === 'true') return;

      el.classList.remove('hide');

      el.addEventListener('click', () => {
        requestAnimationFrame(() => {
          this.autoplayTimerStop();
          this.navUpdate(el.dataset.nav);
        });

        this.classList.add('interacted');
      });
    })
  }

  navUpdate(direction) {
    const nextIndex = this.indexActive + 1 > this.indexLast ? 0 : this.indexActive + 1;
    const prevIndex = this.indexActive - 1 < 0 ? this.indexLast : this.indexActive - 1;
    this.showItem(direction === 'prev' ? prevIndex : nextIndex);
  }

  now(timezone) {
    return Date.parse(typeof timezone !== 'undefined' ? new Date().toLocaleString('en-US', {timeZone: timezone}) : new Date())
  }

  updateFreeShippingBanner(total) {
    const away = Math.abs(total - window.freeShipping?.amount);
    const hasFreeShipping = total >= window.freeShipping?.amount;

    this.freeShippingMessages.forEach(el => {
      const type = el.dataset.freeShippingMessage;

      if (hasFreeShipping) {
        el.classList[type === 'met' ? 'remove' : 'add']('hide');
      } else {
        el.classList[type === 'met' ? 'add' : 'remove']('hide');
        this.freeShippingThresholds.forEach(el => el.innerHTML = window.formatMoney(away).replace('.00', ''));
      }
    });
  }

  /***
  * Countdown Timers
  */
  handleCountdowns() {
    this.countdownTimers = [];
    this.countdownEls.forEach((el, index) => {
      let data = null;

      try {
        // Run times are set in the Announcement Item block
        data = JSON.parse(el.parentElement.dataset.runTimes);
      } catch (error) {
        console.error('Unable to parse countdown settings:', error);
      }

      if (data) {
        const ends = new Date(data.end).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
        const starts = new Date(data.start).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });

        this.countdownCreate({
          el,
          ends,
          endDate: data.end ? new Date(ends).getTime() : null,
          startDate: data.start ? new Date(starts).getTime() : null,
          timezone: 'America/Los_Angeles',
        }, index);
      }
    });
  }

  /**
  * Create countdown
  */
  countdownCreate(settings, index) {
    const endDate = settings.endDate + this.timezoneOffset(settings);
    const today = this.now(settings.timezone);
    const dateHasPassed = Math.sign(endDate - today) >= 0;

    settings.el.title = `Ends ${settings.ends}`;

    if (settings.endDate && dateHasPassed) {
      this.countdownUpdate(settings, settings.el);
      this.countdownTimers[index] = setInterval(() => this.countdownUpdate(settings, settings.el, index), 1000);
    } else {
      settings.el.innerHTML = `
        <span class="countdown__hours">00h:</span>
        <span class="countdown__minutes">00m:</span>
        <span class="countdown__seconds">00s</span>
      `;
    }
  }

  /**
  * Update Timer
  */
  countdownUpdate(settings, $timer, index) {
    const now = this.now(settings.timezone);
    const endDate = settings.endDate + this.timezoneOffset(settings);
    const distance = endDate - now;

    const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const seconds = Math.floor((distance % (1000 * 60)) / 1000)
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });

    if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
      $timer.innerHTML = `
        <span class="countdown__hours">00h:</span>
        <span class="countdown__minutes">00m:</span>
        <span class="countdown__seconds">00s</span>
      `;
      clearInterval(this.timers[index]);
    } else {
      $timer.innerHTML = `
        ${days < 1 ? '' : `<span class="countdown__days">${days}d:</span>`}
        <span class="countdown__hours">${hours}h:</span>
        <span class="countdown__minutes">${minutes}m</span>
        ${days > 0 ? '' : `:<span class="countdown__seconds">${seconds}s</span>`}
      `;
    }
  }

  timezoneOffset(settings) {
    return Math.abs(this.now(settings.timezone) - this.now()) // difference in milliseconds
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
